/**
 * PersonalPrepFieldsInput Component
 *
 * Content-only component for entering daily personal preparation values during
 * the pre-trading ritual. Displays configured fields from PreTradingSettings
 * and allows users to enter today's values.
 *
 * This component renders just the inputs (no Card wrapper) - the parent
 * component is responsible for providing the Card chrome.
 *
 * This is different from PersonalPrepFieldsCard (settings) which configures
 * WHAT fields exist - this component lets users enter TODAY'S values.
 *
 * Supports four field types:
 * - checkbox: Boolean toggle
 * - text: Free-form text input
 * - number: Numeric input with validation
 * - dropdown: Single-select from predefined options (clearable)
 */

import React, { useCallback } from 'react';
import {
  Checkbox,
  Input,
  NumberInput,
  Select,
  SelectItem,
  EmptyState,
} from '@enfomentis/ui';
import type { DropdownOption } from '@/types/shared';

/**
 * Personal prep field configuration from PreTradingSettings
 */
export interface PersonalPrepFieldConfig {
  id: string;
  name: string;
  description?: string;
  type: 'checkbox' | 'dropdown' | 'text' | 'number';
  options?: DropdownOption[];
}

/**
 * Value type for personal prep fields
 * undefined = unset (user hasn't interacted)
 */
export type PersonalPrepValue = boolean | string | number | undefined;

/**
 * Props for PersonalPrepFieldsInput component
 */
export interface PersonalPrepFieldsInputProps {
  /** Configured fields from PreTradingSettings.personalPrepFields */
  fields: PersonalPrepFieldConfig[];
  /** Current values (field id → value, undefined = unset) */
  values: Record<string, PersonalPrepValue>;
  /** Callback when values change */
  onValuesChange: (values: Record<string, PersonalPrepValue>) => void;
}

/**
 * Normalize HeroUI NumberInput value to our expected type.
 * NumberInput's onValueChange can return number | number[] | null,
 * but we only expect single number values.
 * Arrays are treated as undefined to avoid incorrect writes.
 */
const normalizeNumberValue = (
  val: number | number[] | null | undefined
): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (Array.isArray(val)) return undefined;
  if (Number.isNaN(val)) return undefined; // Guard against NaN values
  return val;
};

/**
 * Normalize HeroUI Select value to our expected type.
 * Select's onSelectionChange returns a Set, but we store single string values.
 * Empty Set = undefined (clearable behavior).
 * Multiple selections = undefined (personal prep fields don't use multi-select).
 */
const normalizeSelectValue = (
  keys: 'all' | Set<React.Key>
): string | undefined => {
  if (keys === 'all') return undefined;
  if (keys.size === 0) return undefined;
  if (keys.size > 1) return undefined; // Guard against accidental multi-select
  const firstKey = keys.values().next().value;
  return typeof firstKey === 'string' ? firstKey : undefined;
};

/**
 * Personal Prep Fields Input Component
 *
 * Renders appropriate controls for each configured field type.
 * Shows an empty state when no fields are configured.
 *
 * Note: This is a content-only component. Parent provides the Card wrapper.
 */
export const PersonalPrepFieldsInput: React.FC<PersonalPrepFieldsInputProps> = ({
  fields,
  values,
  onValuesChange,
}) => {
  /**
   * Handle value change for a specific field
   */
  const handleValueChange = useCallback(
    (fieldId: string, value: PersonalPrepValue) => {
      onValuesChange({
        ...values,
        [fieldId]: value,
      });
    },
    [values, onValuesChange]
  );

  /**
   * Render a checkbox field
   */
  const renderCheckboxField = (field: PersonalPrepFieldConfig) => {
    const currentValue = values[field.id];
    // Display as unchecked when undefined, but only write true/false on interaction
    const isSelected = currentValue === true;

    return (
      <div key={field.id} className="w-full">
        <Checkbox
          isSelected={isSelected}
          onValueChange={(checked) => handleValueChange(field.id, checked)}
          classNames={{
            base: 'min-h-[44px] items-start',
            wrapper: 'mt-1',
          }}
        >
          <div className="flex flex-col">
            <span className="text-base">{field.name}</span>
            {field.description && (
              <span className="text-sm text-text-tertiary">
                {field.description}
              </span>
            )}
          </div>
        </Checkbox>
      </div>
    );
  };

  /**
   * Render a text input field
   */
  const renderTextField = (field: PersonalPrepFieldConfig) => {
    const currentValue = values[field.id];
    // Display as empty when undefined, write actual value on interaction
    const displayValue = typeof currentValue === 'string' ? currentValue : '';

    return (
      <div key={field.id} className="w-full">
        <Input
          label={field.name}
          description={field.description}
          value={displayValue}
          onValueChange={(val) => handleValueChange(field.id, val)}
          classNames={{
            input: 'text-base',
            inputWrapper: 'min-h-[44px]',
          }}
        />
      </div>
    );
  };

  /**
   * Render a number input field
   */
  const renderNumberField = (field: PersonalPrepFieldConfig) => {
    const currentValue = values[field.id];
    const numericValue =
      typeof currentValue === 'number' ? currentValue : undefined;

    return (
      <div key={field.id} className="w-full">
        <NumberInput
          label={field.name}
          description={field.description}
          value={numericValue}
          onValueChange={(val) =>
            handleValueChange(field.id, normalizeNumberValue(val))
          }
          hideStepper
          classNames={{
            input: 'text-base',
            inputWrapper: 'min-h-[44px]',
          }}
        />
      </div>
    );
  };

  /**
   * Render a dropdown/select field
   */
  const renderDropdownField = (field: PersonalPrepFieldConfig) => {
    const currentValue = values[field.id];
    const stringValue =
      typeof currentValue === 'string' ? currentValue : undefined;
    const options = field.options ?? [];

    return (
      <div key={field.id} className="w-full">
        <Select
          label={field.name}
          description={field.description}
          placeholder="Select an option"
          selectedKeys={stringValue ? new Set([stringValue]) : new Set()}
          onSelectionChange={(keys) =>
            handleValueChange(field.id, normalizeSelectValue(keys))
          }
          classNames={{
            trigger: 'min-h-[44px]',
            value: 'text-base',
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.label}</SelectItem>
          ))}
        </Select>
      </div>
    );
  };

  /**
   * Render a field based on its type
   */
  const renderField = (field: PersonalPrepFieldConfig) => {
    switch (field.type) {
      case 'checkbox':
        return renderCheckboxField(field);
      case 'text':
        return renderTextField(field);
      case 'number':
        return renderNumberField(field);
      case 'dropdown':
        return renderDropdownField(field);
      default:
        return null;
    }
  };

  // Empty state when no fields configured
  if (fields.length === 0) {
    return (
      <EmptyState
        title="No personal prep fields configured"
        description="Configure personal preparation fields in Settings to track your daily routine"
      />
    );
  }

  // Fields input list
  return (
    <div className="space-y-4">
      {fields.map((field) => renderField(field))}
    </div>
  );
};

export default PersonalPrepFieldsInput;
